<template>
  <div class="asset-view-wrapper">
    <div class="assets-table-container">
      <div class="assets-grid-card">
        <div class="assets-grid-card__header">
          <div class="assets-grid-title">
            <div class="assets-grid-title__icon">
              <i class="bi bi-diagram-3-fill" />
            </div>
            <div>
              <div class="assets-grid-title__text">
                {{ $t('aside.menu.pages.assetSchemes') }}
              </div>
              <div class="text-muted small">
                {{ $t('components.assetSchemes.subtitle') }}
              </div>
            </div>
          </div>

          <div class="assets-grid-controls">
            <button
              type="button"
              class="assets-toolbar-btn"
              :title="$t('components.assets.dataGrids.refresh')"
              @click="fetchList"
            >
              <i class="bi bi-arrow-clockwise" />
              <span>{{ $t('components.assets.dataGrids.refresh') }}</span>
            </button>
          </div>
        </div>

        <div class="assets-grid-card__body">
          <div
            v-if="listError"
            class="alert alert-danger d-flex align-items-center gap-2 mb-3"
          >
            <i class="bi bi-exclamation-triangle-fill" />
            <span>{{ listError }}</span>
          </div>
          <div
            v-if="listLoading"
            class="wp-loading"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.assetSchemes.loading') }}</span>
          </div>
          <div v-else>
            <DxDataGrid
              class="schemes-grid"
              :data-source="listData"
              :hover-state-enabled="true"
              :column-auto-width="false"
              :show-borders="false"
              :show-column-lines="false"
              :show-row-lines="false"
              :row-alternation-enabled="true"
              :row-rendering-mode="'standard'"
              :column-hiding-enabled="true"
              :word-wrap-enabled="true"
            >
              <DxHeaderFilter :visible="true" />
              <DxFilterRow :visible="true" />
              <DxPaging :page-size="20" />
              <DxPager
                :allowed-page-sizes="[10, 20, 50]"
                :show-info="true"
                :show-page-size-selector="true"
                :show-navigation-buttons="true"
              />

              <DxColumn
                data-field="name"
                :caption="$t('components.assetSchemes.columns.name')"
                :min-width="180"
              />
              <DxColumn
                data-field="groupName"
                :caption="$t('components.assetSchemes.columns.group')"
                :min-width="160"
              />
              <DxColumn
                data-field="preview"
                :caption="$t('components.assetSchemes.columns.preview')"
                width="160"
                :allow-filtering="false"
                :allow-sorting="false"
                cell-template="preview"
                alignment="center"
              />
              <DxColumn
                data-field="markersCount"
                :caption="$t('components.assetSchemes.columns.markers')"
                alignment="center"
                width="140"
                :allow-filtering="false"
              />
              <DxColumn
                :caption="$t('components.assetSchemes.columns.actions')"
                :width="140"
                :allow-filtering="false"
                :allow-sorting="false"
                cell-template="actions-template"
              />

              <template #preview="{ data }">
                <div class="scheme-thumb">
                  <img
                    v-if="data.data.preview"
                    :src="data.data.preview"
                    alt="Scheme preview"
                  >
                  <span
                    v-else
                    class="text-muted"
                  >-</span>
                </div>
              </template>

              <template #actions-template="{ data }">
                <div class="action-buttons">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    :title="$t('components.assetSchemes.actions.view')"
                    @click="openPreview(data.data)"
                  >
                    <i class="bi bi-eye" />
                  </button>
                  <button
                    v-if="!isReadOnly && canEdit"
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :title="$t('components.assetSchemes.actions.edit')"
                    @click="openEditor(data.data)"
                  >
                    <i class="bi bi-pencil" />
                  </button>
                </div>
              </template>
            </DxDataGrid>
            <div
              v-if="!listData.length"
              class="text-center text-muted py-3"
            >
              {{ $t('components.assetSchemes.empty') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No modal: edit/preview are separate views -->
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import {
  DxDataGrid,
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxPaging,
  DxPager
} from 'devextreme-vue/data-grid'
import { getAssetSchemes } from '@/crud/asset-scheme'
import { canEditAssetGroups, isRestrictedAssetUser } from '@/utils/tools'

type SchemeRow = {
  id: number
  name: string
  groupId: number
  groupName: string
  filePath: string | null
  preview: string | null
  markersJson: any[] | null
  markersCount: number
}

export default defineComponent({
  name: 'AssetSchemesView',
  components: {
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxPaging,
    DxPager
  },
  emits: ['pageName'],
  data() {
    return {
      listData: [] as SchemeRow[],
      listLoading: false,
      listError: null as string | null,
      assetsLoading: false
    }
  },
  computed: {
    canEdit(): boolean {
      return canEditAssetGroups()
    },
    isReadOnly(): boolean {
      return isRestrictedAssetUser()
    }
  },
  mounted() {
    this.setTitle()
    this.fetchList()
  },
  watch: {
    '$i18n.locale'() {
      this.setTitle()
    }
  },
  methods: {
    setTitle() {
      this.$emit('pageName', this.$t('aside.menu.pages.assetSchemes'))
    },
    convertPathToUrl(filePath: string): string {
      let relativePath = filePath
      if (filePath.includes('/public/var/uploads/')) {
        relativePath = filePath.substring(filePath.indexOf('/var/uploads/'))
      } else if (filePath.includes('var/uploads/')) {
        const index = filePath.indexOf('var/uploads/')
        relativePath = '/' + filePath.substring(index)
      }

      if (!relativePath.startsWith('/')) {
        relativePath = '/' + relativePath
      }

      const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '')
      return base ? `${base}${relativePath}` : relativePath
    },
    async fetchList() {
      this.listLoading = true
      this.listError = null
      try {
        const response = await getAssetSchemes({ skip: 0, take: 200 })
        const data = response.data?.result || response.data?.data?.result || response.data?.result || []
        const schemes = Array.isArray(data) ? data : (response.data?.result || [])

        this.listData = schemes.map((scheme: any) => {
          const markersJson = Array.isArray(scheme.markersJson) ? scheme.markersJson : []
          const previewPath = scheme.filePath || scheme.file_path
          return {
            id: scheme.id,
            name: scheme.name,
            groupId: scheme.group?.id,
            groupName: scheme.group?.name || '-',
            filePath: previewPath || null,
            preview: previewPath ? this.convertPathToUrl(previewPath) : null,
            markersJson,
            markersCount: markersJson.length
          }
        })
      } catch (e: any) {
        this.listError = e?.response?.data?.error || e?.message || this.$t('components.assetSchemes.error')
      } finally {
        this.listLoading = false
      }
    },
    openEditor(row: SchemeRow) {
      this.$router.push({ name: 'AssetSchemeEdit', params: { id: row.id } })
    },
    openPreview(row: SchemeRow) {
      this.$router.push({ name: 'AssetSchemePreview', params: { id: row.id } })
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-schemes-view.scss';
</style>
<style src="@/assets/scss/asset-view-style.scss" lang="scss"></style>
